import styled, { css, keyframes } from "styled-components";

/**
 * Self-contained drawer for the Notifications and Active Users panels.
 *
 * Deliberately NOT built on react-burger-menu: that library's classes are
 * styled globally from `styled.js`'s Wrapper (`.bm-menu` hard-codes a #373a47
 * background, 2.5em/1.5em padding and 1.15em font), so anything rendered inside
 * inherited a fixed dark slab local styles could not override.
 *
 * Every class is prefixed `np-` so no global rule in App.css or styled.js can
 * reach in, and nothing here leaks out. The app's theme context exposes only
 * `mode`, so this owns its palette.
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to   { opacity: 1; transform: none; }
`;

const palette = (dark) =>
  dark
    ? css`
        --np-surface: #0f1218;
        --np-raised: #161a22;
        --np-sunk: #1b2029;
        --np-ink: #eef1f6;
        --np-ink-2: #aab3c2;
        --np-muted: #7d8899;
        --np-line: #232936;
        --np-accent: #6aa5ff;
        --np-accent-weak: rgba(106, 165, 255, 0.13);
        --np-shadow: rgba(0, 0, 0, 0.62);
        --np-scrim: rgba(6, 9, 14, 0.58);
      `
    : css`
        /* The list sits on a deliberately deeper grey than the cards. An
           almost-white background made white cards invisible and the whole
           panel read as one blank sheet. */
        --np-surface: #ffffff;
        --np-raised: #ffffff;
        --np-sunk: #eceff5;
        --np-ink: #0d1420;
        --np-ink-2: #46536a;
        --np-muted: #7d8b9f;
        --np-line: #dde3ec;
        --np-accent: #2f6fed;
        --np-accent-weak: rgba(47, 111, 237, 0.08);
        --np-shadow: rgba(13, 20, 32, 0.2);
        --np-scrim: rgba(13, 20, 32, 0.34);
      `;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: ${({ dark }) =>
    dark ? "rgba(6,9,14,0.58)" : "rgba(13,20,32,0.34)"};
  backdrop-filter: blur(2px);
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
  transition: opacity 0.22s ease;
`;

export const Drawer = styled.aside`
  ${({ dark }) => palette(dark)}

  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1301;
  width: min(420px, 100vw);
  display: flex;
  flex-direction: column;
  background: var(--np-surface);
  color: var(--np-ink);
  box-shadow: -20px 0 50px -26px var(--np-shadow);
  transform: translateX(${({ open }) => (open ? "0" : "100%")});
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0.24, 1);
  font-family: inherit;
  font-size: 13px;
  line-height: 1.45;

  /* ── row 1: identity + controls, one line ── */
  .np-bar {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 52px;
    padding: 0 10px 0 18px;
    border-bottom: 1px solid var(--np-line);
  }
  .np-h {
    font-size: 14.5px;
    font-weight: 640;
    letter-spacing: -0.012em;
    margin: 0;
    color: var(--np-ink);
    text-transform: none;
    white-space: nowrap;
  }
  .np-pill {
    font-size: 10.5px;
    font-weight: 700;
    line-height: 1;
    padding: 3px 6px;
    border-radius: 20px;
    background: var(--np-accent);
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
  .np-pill.quiet {
    background: var(--np-sunk);
    color: var(--np-muted);
  }
  .np-spring {
    flex: 1 1 auto;
  }
  .np-icon {
    flex: none;
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: var(--np-ink-2);
    cursor: pointer;
    transition: background 0.14s ease, color 0.14s ease;
    padding: 0;
  }
  .np-icon:hover {
    background: var(--np-sunk);
    color: var(--np-ink);
  }
  .np-icon[aria-pressed="true"] {
    background: var(--np-accent-weak);
    color: var(--np-accent);
  }
  .np-icon svg {
    width: 15px;
    height: 15px;
    display: block;
  }
  .np-text-btn {
    flex: none;
    border: 0;
    background: transparent;
    color: var(--np-accent);
    font-family: inherit;
    font-size: 12px;
    font-weight: 580;
    padding: 5px 7px;
    border-radius: 7px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.14s ease;
  }
  .np-text-btn:hover {
    background: var(--np-accent-weak);
  }

  /* ── row 2: segmented control, scrolls sideways if it must ── */
  .np-seg {
    flex: none;
    display: flex;
    gap: 2px;
    padding: 7px 10px 7px 14px;
    overflow-x: auto;
    scrollbar-width: none;
    border-bottom: 1px solid var(--np-line);
  }
  .np-seg::-webkit-scrollbar {
    display: none;
  }
  .np-tab {
    position: relative;
    flex: none;
    border: 0;
    background: transparent;
    color: var(--np-muted);
    font-family: inherit;
    font-size: 12px;
    font-weight: 550;
    padding: 5px 7px;
    border-radius: 7px;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 0.14s ease, background 0.14s ease;
  }
  .np-tab:hover {
    color: var(--np-ink);
    background: var(--np-sunk);
  }
  .np-tab[aria-selected="true"] {
    color: var(--np-ink);
    background: var(--np-sunk);
  }
  .np-tab .np-c {
    font-size: 10.5px;
    font-variant-numeric: tabular-nums;
    color: var(--np-muted);
  }
  .np-tab[aria-selected="true"] .np-c {
    color: var(--np-accent);
    font-weight: 700;
  }

  /* ── search: replaces the bar contents instead of adding a row ── */
  .np-find {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 52px;
    padding: 0 10px 0 16px;
    border-bottom: 1px solid var(--np-line);
    animation: ${fadeUp} 0.16s ease;
  }
  .np-find input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 13.5px;
    color: var(--np-ink);
    outline: none;
    padding: 0;
  }
  .np-find input::placeholder {
    color: var(--np-muted);
  }

  /* ── list ──
     Sunk background so the cards below read as raised surfaces. On plain white
     the rows ran together and the panel looked like an unformatted page. */
  .np-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    background: var(--np-sunk);
    padding: 0 11px 18px;
  }
  .np-list::-webkit-scrollbar {
    width: 9px;
  }
  .np-list::-webkit-scrollbar-thumb {
    background: var(--np-line);
    border-radius: 99px;
    border: 3px solid var(--np-sunk);
  }

  .np-day {
    position: sticky;
    top: 0;
    z-index: 2;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--np-muted);
    padding: 11px 5px 6px;
    background: var(--np-sunk);
  }

  /* ── card ── */
  .np-row {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 11px 12px;
    margin-bottom: 7px;
    border-radius: 10px;
    background: var(--np-raised);
    border: 1px solid var(--np-line);
    box-shadow: 0 1px 1.5px
      ${({ dark }) => (dark ? "rgba(0,0,0,0.32)" : "rgba(13,20,32,0.04)")};
    overflow: hidden;
    transition: border-color 0.14s ease, box-shadow 0.14s ease,
      transform 0.14s ease;
  }
  .np-row::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: transparent;
  }
  .np-row:hover {
    border-color: ${({ dark }) =>
      dark ? "rgba(255,255,255,0.16)" : "rgba(13,20,32,0.14)"};
    box-shadow: 0 3px 10px -4px
      ${({ dark }) => (dark ? "rgba(0,0,0,0.6)" : "rgba(13,20,32,0.14)")};
    transform: translateY(-1px);
  }
  /* Unread is marked by the rail and weight alone. A background tint looked
     fine on one card and turned the whole panel blue when most were unread. */
  .np-row.is-unread::before {
    background: var(--np-accent);
  }
  .np-row.is-unread .np-who,
  .np-row.is-unread .np-said strong {
    color: var(--np-ink);
  }
  .np-row.is-unread .np-said {
    color: var(--np-ink);
  }

  /* type glyph: gives each card an anchor, so a column of rows from the same
     person is still scannable by what happened rather than who did it */
  .np-ico {
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    background: var(--np-sunk);
    color: var(--np-muted);
  }
  .np-ico.action {
    background: rgba(217, 119, 6, 0.12);
    color: #d97706;
  }
  .np-ico.good {
    background: rgba(5, 150, 105, 0.12);
    color: #059669;
  }
  .np-ico.bad {
    background: rgba(220, 38, 38, 0.11);
    color: #dc2626;
  }
  .np-ico.info {
    background: var(--np-accent-weak);
    color: var(--np-accent);
  }

  .np-main {
    flex: 1 1 auto;
    min-width: 0;
  }
  .np-line1 {
    display: flex;
    align-items: baseline;
    gap: 5px;
    color: var(--np-ink);
    font-size: 13px;
  }
  .np-who {
    font-weight: 650;
    white-space: nowrap;
  }
  /* Wraps to a second line rather than truncating: the sentence carries the
     meaning, and a card has room for it. */
  .np-said {
    color: var(--np-ink-2);
    min-width: 0;
    line-height: 1.4;
  }
  .np-said strong {
    color: var(--np-ink);
    font-weight: 600;
  }
  .np-when {
    flex: none;
    margin-left: auto;
    font-size: 11px;
    color: var(--np-muted);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    padding-left: 6px;
  }
  .np-line2 {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 3px;
    font-size: 11.5px;
    color: var(--np-muted);
  }
  .np-kind {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
  }
  .np-kind::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
  .np-kind.action {
    color: #d97706;
  }
  .np-kind.good {
    color: #059669;
  }
  .np-kind.bad {
    color: #dc2626;
  }
  .np-kind.info {
    color: var(--np-accent);
  }

  /* actions stay out of the way until the row is engaged with */
  .np-do {
    display: flex;
    gap: 6px;
    margin-top: 7px;
  }
  .np-btn {
    font-family: inherit;
    font-size: 11.5px;
    font-weight: 560;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid var(--np-line);
    background: var(--np-surface);
    color: var(--np-ink);
    cursor: pointer;
    transition: all 0.13s ease;
  }
  .np-btn:hover {
    border-color: var(--np-muted);
  }
  .np-btn.primary {
    background: var(--np-accent);
    border-color: var(--np-accent);
    color: #fff;
  }
  .np-btn.primary:hover {
    filter: brightness(1.08);
  }

  /* ── presence ── */
  .np-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--np-muted);
    flex: none;
  }
  .np-dot.on {
    background: #12b76a;
  }
  /* Idle (#264): logged in, socket connected, just no activity within the
     window - distinct from both "working now" and "logged out". Reuses the
     amber already used elsewhere in this file rather than a new color. */
  .np-dot.idle {
    background: #d97706;
  }
  /* Whoever is working right now is the reason this panel gets opened, so the
     online rows carry a rail and colour and the rest recede. */
  .np-row.is-online::before {
    background: #12b76a;
  }
  .np-row.is-online .np-line2 {
    color: #067a48;
    font-weight: 560;
  }
  .np-row.is-idle::before {
    background: #d97706;
  }
  .np-row.is-idle .np-line2 {
    color: #d97706;
    font-weight: 560;
  }
  .np-row.is-off {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
  }
  .np-row.is-off:hover {
    background: var(--np-raised);
    border-color: var(--np-line);
  }
  .np-row.is-off .np-avatar-dim {
    opacity: 0.62;
    filter: saturate(0.5);
  }

  /* ── empty ── */
  .np-none {
    text-align: center;
    padding: 60px 24px;
    color: var(--np-muted);
    font-size: 12.5px;
    animation: ${fadeUp} 0.2s ease;
  }
  .np-none b {
    display: block;
    color: var(--np-ink-2);
    font-size: 13.5px;
    font-weight: 600;
    margin-bottom: 3px;
  }

  @media (max-width: 480px) {
    width: 100vw;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    .np-row,
    .np-none,
    .np-find {
      animation: none;
      transition: none;
    }
  }
`;
